import React from 'react';
import { Link } from 'react-router-dom';
import { Droplet, CheckCircle2, Clock, Shield, Target, ArrowRight } from 'lucide-react';
import WorkGallery from '../../components/WorkGallery';

const StoneChipTouchUp = () => {
  const workImages = [
    '/siteimages/image_0012.jpg',
    '/siteimages/image_0017.jpeg',
    '/siteimages/image_0027.jpg',
    '/siteimages/image_0037.jpg',
  ];

  return (
    <div>
      <section className="bg-gradient-to-r from-dark-navy to-dark-teal text-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-3 mb-4">
            <Droplet className="w-12 h-12 text-bmw-blue" />
            <h1 className="text-4xl md:text-5xl font-heading font-bold">
              Stone Chip & Touch Up
            </h1>
          </div>
          <p className="text-lg md:text-xl text-soft-grey max-w-3xl">
            Precision stone chip repair and touch-up services to maintain your vehicle's appearance and protect against corrosion.
          </p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-heading font-bold text-dark-navy mb-6">
                Professional <span className="text-bmw-blue">Touch-Up Services</span>
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Stone chips and small paint blemishes may seem minor, but left untreated, they can lead to rust 
                and more extensive damage. Our precision touch-up service addresses these imperfections using 
                color-matched paint and professional application techniques.
              </p>
              <p className="text-gray-600 mb-6 leading-relaxed">
                We carefully prepare each chip by cleaning and priming, then apply perfectly matched paint in 
                thin layers for an invisible repair. Our process protects your vehicle from corrosion while 
                maintaining its aesthetic appeal.
              </p>

              <div className="bg-bmw-blue bg-opacity-10 border-l-4 border-bmw-blue p-6 rounded-r-lg">
                <h3 className="font-bold text-dark-navy mb-2 flex items-center gap-2">
                  <Target className="w-5 h-5 text-bmw-blue" />
                  Precision Repair
                </h3>
                <p className="text-gray-600">
                  Expert color matching and meticulous application ensure your stone chip repairs blend seamlessly with the surrounding paint.
                </p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-off-white p-6 rounded-lg">
                <h3 className="text-xl font-bold text-dark-navy mb-4">What We Fix</h3>
                <ul className="space-y-3">
                  {[
                    'Stone chips on bonnet and front bumper',
                    'Road debris damage',
                    'Minor paint chips',
                    'Small paint blemishes',
                    'Edge chips on doors',
                    'Minor scratches',
                    'Paint touch-up after repairs',
                    'Preventative corrosion protection',
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-bmw-blue flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-racing-red bg-opacity-10 border border-racing-red p-6 rounded-lg">
                <h3 className="font-bold text-dark-navy mb-3">Touch-Up Quote</h3>
                <p className="text-gray-600 mb-4">
                  Get a competitive quote for stone chip repair and paint touch-up services.
                </p>
                <Link 
                  to="/contact" 
                  className="inline-flex items-center gap-2 bg-racing-red text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transition"
                >
                  Get Free Quote
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-dark-navy text-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-bmw-blue rounded-full mb-4">
                <Clock className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold mb-2">Quick Service</h3>
              <p className="text-soft-grey">
                Most touch-up work completed within 1-2 days for fast results.
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-bmw-blue rounded-full mb-4">
                <Shield className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold mb-2">Corrosion Protection</h3>
              <p className="text-soft-grey">
                Proper repair prevents rust and protects your vehicle's value.
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-bmw-blue rounded-full mb-4">
                <Target className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold mb-2">Perfect Match</h3>
              <p className="text-soft-grey">
                Color-matched paint for invisible, professional touch-up repairs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Work Gallery */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <WorkGallery images={workImages} title="Stone Chip Repair Gallery" />
        </div>
      </section>

      <section className="py-16 bg-bmw-blue text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
            Need Stone Chip Repair?
          </h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">
            Don't let stone chips lead to rust. Contact us for professional touch-up services today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="bg-racing-red text-white px-8 py-4 rounded-lg font-semibold hover:bg-red-700 transition shadow-lg inline-flex items-center justify-center gap-2"
            >
              Get Free Quote
              <ArrowRight />
            </Link>
            <a 
              href="tel:08000452446" 
              className="bg-white text-bmw-blue px-8 py-4 rounded-lg font-semibold hover:bg-off-white transition shadow-lg"
            >
              Call: 0800 045 2446
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default StoneChipTouchUp;
